import { Badge } from 'antd';
import {
  useCheckMemberSupplier,
  useCheckPermissions,
  useGetProductEnums,
  useGetProductFullView,
} from 'hooks';

import { useQueryProductItemModules } from 'pages/product-full-view/components/product-detail-view/hooks';

import {
  QaSpecIngredient,
  QaSpecDocsAndCertificates,
  QaSpecProduct,
  QaSpecManufacturingProcess,
  QaFormula,
  QaAllergen,
  QaSpecComponent,
  QaSpecNutritionData,
  QaSpecContacts,
  QaFreshSeafood,
  QaFreshMeat,
  QaPackaging,
  QaClaims,
  QaFreshProduce,
  QaFreshDeli,
  QaSpecProductContain,
  QaPetFood,
  QaCorrectiveAction,
  TraceableComments,
} from 'pages/qa-spec/components';

import { useQaTabChecker } from 'pages/qa-spec/components/edit-tab-checker';
import {
  PERMISSION_VIEW_QA_SPEC_CARS,
  useCheckAllowEditComments,
} from 'pages/qa-spec/hooks';
import {
  useTraceableCommentCount,
  useUpdateCommentCountOnTabClick,
} from './hooks';
import { useCheckIsSuperAdmin } from 'hooks';
import ProductPackaging from 'pages/qa-spec/components/packaging/ProductPackaging';

const EditItemCount = ({ tabKey, children }) => {
  const { editItemCountByTab } = useQaTabChecker();

  return (
    <Badge count={editItemCountByTab?.[`${tabKey}`]} offset={[15, 4]}>
      {children && children}
    </Badge>
  );
};

const qaPackagingRender = ({ productFull }) => ({
  key: 'labeling',
  icon: (
    <EditItemCount tabKey='labeling'>
      <i class='fa-solid fa-box-open'></i>
    </EditItemCount>
  ),
  des: 'Labeling',
  content: (isTabActive) => (
    <QaPackaging productFull={productFull} isTabActive={isTabActive} />
  ),
  visible: true,
});

const qaClaimsRender = (type, ownerId) => ({
  key: 'claims',
  icon: (
    <EditItemCount tabKey='claims'>
      <i class='fa-solid fa-file-circle-check'></i>
    </EditItemCount>
  ),
  des: 'Claims',
  content: (isTabActive) => <QaClaims isTabActive={isTabActive} ownerId={ownerId} />,
  visible: true,
});

export const useGetTabListByQaType = ({ type, productId }) => {
  const { productFull, isLoading: productLoading } = useGetProductFullView({
    productId,
  });
  const { productEnums } = useGetProductEnums();

  const hasViewCARsPermission = useCheckPermissions(
    PERMISSION_VIEW_QA_SPEC_CARS
  );
  const hasEditCommentPermission = useCheckAllowEditComments();

  const { productItemModules, refetchProductItems } =
    useQueryProductItemModules(productId);

  const ownerId = productFull?.ownerId;
  const isSupplierUser = useCheckMemberSupplier();
  const isSuperAdmin = useCheckIsSuperAdmin();

  const rawCount = useTraceableCommentCount({ productId, ownerId });
  const count = isSuperAdmin ? { count: 0 } : rawCount;
  const { handleCommentsTabClick } = useUpdateCommentCountOnTabClick({
    productId,
    ownerId,
  });

  return [
    {
      key: 'product-specification',
      icon: (
        <EditItemCount tabKey='product-specification'>
          <i class='fa-solid fa-clipboard-list'></i>
        </EditItemCount>
      ),
      des: 'Product Specification',
      content: (isTabActive) => (
        <QaSpecProduct
          refetchProductItems={refetchProductItems}
          productLoading={productLoading}
          productId={productId}
          productFull={productFull}
        />
      ),
      visible: true,
    },
    {
      key: 'contacts',
      icon: (
        <EditItemCount tabKey='contacts'>
          <i class='fa-solid fa-address-book'></i>
        </EditItemCount>
      ),
      des: 'Contacts, Locations & Certificates',
      content: (isTabActive) => <QaSpecContacts id={productId} />,
      visible: true,
    },
    {
      key: 'fresh-seafood',
      icon: (
        <EditItemCount tabKey='fresh-seafood'>
          <i class='fa-solid fa-fish-fins'></i>
        </EditItemCount>
      ),
      des: 'Fresh - Seafood',
      content: (isTabActive) => <QaFreshSeafood isTabActive={isTabActive} />,
      visible: type === 'Fresh - Seafood',
    },
    {
      key: 'fresh-meat',
      icon: (
        <EditItemCount tabKey='fresh-meat'>
          <i class='fa-solid fa-cow'></i>
        </EditItemCount>
      ),
      des: 'Fresh - Meat',
      content: (isTabActive) => <QaFreshMeat isTabActive={isTabActive} />,
      visible: type === 'Fresh - Meat',
    },
    {
      key: 'fresh-produce',
      icon: (
        <EditItemCount tabKey='fresh-produce'>
          <i class='fa-solid fa-apple-whole'></i>
        </EditItemCount>
      ),
      des: 'Fresh - Produce',
      content: (isTabActive) => <QaFreshProduce />,
      visible: type === 'Fresh - Produce',
    },

    {
      key: 'fresh-deli',
      icon: (
        <EditItemCount tabKey='fresh-deli'>
          <i class='fa-solid fa-cheese'></i>
        </EditItemCount>
      ),
      des: 'Fresh - Deli',
      content: (isTabActive) => <QaFreshDeli />,
      visible: type === 'Fresh - Deli',
    },
    {
      key: 'ingredients',
      icon: (
        <EditItemCount tabKey='ingredients'>
          <i class='fa-solid fa-leaf'></i>
        </EditItemCount>
      ),
      des: 'Ingredients',
      content: (isTabActive) => (
        <>{isTabActive && <QaSpecIngredient productId={productId} />}</>
      ),
      visible: type !== 'HBC / OTC' && type !== 'Non-Food',
    },
    {
      key: 'allergens',
      icon: (
        <EditItemCount tabKey='allergens'>
          <i class='fa-solid fa-person-dots-from-line'></i>
        </EditItemCount>
      ),
      des: 'Allergens',
      content: (isTabActive) => (
        <>
          {isTabActive && (
            <QaAllergen
              productEnums={productEnums}
              productItemModules={productItemModules}
              isTabActive={isTabActive}
            />
          )}
        </>
      ),
      visible: true,
    },
    {
      key: 'components',
      icon: (
        <EditItemCount tabKey='components'>
          <i class='fa-solid fa-table-columns'></i>
        </EditItemCount>
      ),
      des: 'Components',
      content: (isTabActive) => <QaSpecComponent productId={productId} />,
      visible: type === 'HBC / OTC' || type === 'Non-Food' || type === 'Pet',
    },
    {
      key: 'facts-panel',
      icon: (
        <EditItemCount tabKey='facts-panel'>
          <i class='fa-solid fa-table'></i>
        </EditItemCount>
      ),
      des: 'Facts Panel',
      content: (isTabActive) => <QaSpecNutritionData productId={productId} />,
      visible: type !== 'Non-Food',
    },
    qaClaimsRender(type, ownerId),
    {
      key: 'pet-food',
      icon: (
        <EditItemCount tabKey='pet-food'>
          <i class='fa-solid fa-bone'></i>
        </EditItemCount>
      ),
      des: 'Pet Food',
      content: (isTabActive) => <QaPetFood />,
      visible: type === 'Pet',
    },
    {
      key: 'formula',
      icon: (
        <EditItemCount tabKey='formula'>
          <i class='fa-solid fa-book'></i>
        </EditItemCount>
      ),
      des: 'Formula',
      content: (activeTab) => {
        return <QaFormula productEnums={productEnums} type={type} />;
      },
      visible: type !== 'Food',
    },
    {
      key: 'manufacturing-process',
      icon: (
        <EditItemCount tabKey='manufacturing-process'>
          <i class='fa-solid fa-industry'></i>
        </EditItemCount>
      ),
      des: 'Manufacturing Process',
      content: (isTabActive) => (
        <QaSpecManufacturingProcess
          productItemModules={productItemModules}
          productEnums={productEnums}
          ownerId={ownerId}
        />
      ),
      visible: true,
    },
    qaPackagingRender({ type, productFull }),
    {
      key: 'documents-certificates',
      icon: (
        <EditItemCount tabKey='documents-certificates'>
          <i class='fa-solid fa-certificate'></i>
        </EditItemCount>
      ),
      des: 'Documents',
      content: (isTabActive) => (
        <>
          {isTabActive && <QaSpecDocsAndCertificates productId={productId} />}
        </>
      ),
      visible: true,
    },
    {
      key: 'corrective-action',
      icon: (
        <EditItemCount tabKey='corrective-action'>
          <i class='fa-solid fa fa-recycle'></i>
        </EditItemCount>
      ),
      des: 'Corrective Actions',
      content: (isTabActive) => (
        <QaCorrectiveAction isTabActive={isTabActive} productId={productId} />
      ),
      visible: hasViewCARsPermission,
    },
    {
      key: 'product-contains',
      icon: (
        <EditItemCount tabKey='product-contains'>
          <i class='fa-solid fa-circle-dot'></i>
        </EditItemCount>
      ),
      des: 'Product Contains',
      content: (activeTab) => <QaSpecProductContain productId={productId} />,
      visible: checkIsDollarProduct(ownerId),
    },
    {
      key: 'comments',
      icon: (
        <Badge count={count.count} size='small' offset={[5, 0]}>
          <i className='fa-solid fa-comments'></i>
        </Badge>
      ),
      des: 'Comments',
      content: (isTabActive) => {
        if (isTabActive && isSupplierUser) {
          handleCommentsTabClick();
        }
        return (
          <TraceableComments isTabActive={isTabActive} productId={productId} />
        );
      },
      visible: hasEditCommentPermission,
    },
    {
      key: 'packaging',
      icon: (
        <EditItemCount tabKey='packaging'>
          <i className='fa-solid fa-box-open'></i>
        </EditItemCount>
      ),
      des: 'Packaging - EPR',
      content: (isTabActive) => {
        return (
          <ProductPackaging
            isTabActive={isTabActive}
            productId={productId}
            enumOptions={enumOptions}
          />
        );
      },
      visible: true,
    },
  ];
};

export const checkIsDollarProduct = (ownerId) => {
  return ownerId === +process.env.REACT_APP_DOLLAR_GENERAL_ID;
};

const enumOptions = {
  lookupsDemo: false,
};
